import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, CheckSquare, XSquare, Edit2, Trash2, X, Check } from 'lucide-react';
import SortableSelectedList from './SortableSelectedList';

export default function TemplatesSection({
  templates,
  selectedInclusions,
  selectedExclusions,
  onToggleInclusion,
  onToggleExclusion,
  onAddTemplate,
  onEditTemplate,
  onDeleteTemplate,
  onReorderInclusions,
  onReorderExclusions,
}) {
  const [newText, setNewText] = useState('');
  const [newCategory, setNewCategory] = useState('inclusion');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const inclusionTemplates = templates.filter(t => t.category === 'inclusion');
  const exclusionTemplates = templates.filter(t => t.category === 'exclusion');

  const handleAddNew = () => {
    if (!newText.trim()) return;
    onAddTemplate(newText.trim(), newCategory);
    setNewText('');
  };

  const startEditing = (template) => {
    setEditingId(template.id);
    setEditText(template.text);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditText('');
  };

  const saveEdit = (template) => {
    if (!editText.trim()) return;
    onEditTemplate(template.id, editText.trim());
    setEditingId(null);
    setEditText('');
  };

  const renderTemplateItem = (t, type) => {
    const isEditing = editingId === t.id;
    const isChecked = type === 'inclusion' 
      ? selectedInclusions.includes(t.text)
      : selectedExclusions.includes(t.text);
    const onToggle = type === 'inclusion' ? onToggleInclusion : onToggleExclusion;
    const hoverClass = type === 'inclusion' ? 'hover:bg-green-100' : 'hover:bg-red-100';
    const checkboxClass = type === 'inclusion' 
      ? 'border-green-500 data-[state=checked]:bg-green-600'
      : 'border-red-500 data-[state=checked]:bg-red-600';

    if (isEditing) {
      return (
        <div key={t.id} className="flex items-center gap-2 p-2 bg-white rounded-lg border border-slate-200">
          <Input 
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            className="flex-1 h-8 text-sm"
            autoFocus
            onKeyPress={(e) => e.key === 'Enter' && saveEdit(t)}
          />
          <Button 
            type="button" 
            size="sm" 
            variant="ghost"
            onClick={() => saveEdit(t)}
            className="h-8 w-8 p-0 text-green-600 hover:bg-green-100"
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button 
            type="button" 
            size="sm" 
            variant="ghost"
            onClick={cancelEditing}
            className="h-8 w-8 p-0 text-slate-500 hover:bg-slate-100"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      );
    }

    return (
      <div key={t.id} className={`group flex items-center justify-between p-2 rounded-lg ${hoverClass} transition-colors`}>
        <label className="flex items-center gap-2 cursor-pointer flex-1">
          <Checkbox 
            checked={isChecked}
            onCheckedChange={() => onToggle(t.text)}
            className={checkboxClass}
          />
          <span className="text-sm text-slate-700">{t.text}</span>
        </label>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button 
            type="button" 
            size="sm" 
            variant="ghost"
            onClick={() => startEditing(t)}
            className="h-7 w-7 p-0 text-slate-500 hover:text-blue-600 hover:bg-blue-50"
          >
            <Edit2 className="h-3.5 w-3.5" />
          </Button>
          <Button 
            type="button" 
            size="sm" 
            variant="ghost"
            onClick={() => onDeleteTemplate(t.id)}
            className="h-7 w-7 p-0 text-slate-500 hover:text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Inclusions */}
        <div>
          <Label className="text-sm font-semibold text-green-700 mb-3 block flex items-center gap-2">
            <CheckSquare className="h-4 w-4" /> Inclusions
          </Label>
          <div className="space-y-1 max-h-72 overflow-y-auto pr-2 bg-green-50/50 p-3 rounded-xl border border-green-100">
            {inclusionTemplates.length === 0 ? (
              <p className="text-sm text-slate-500 italic py-4 text-center">No inclusions available. Add some below.</p>
            ) : (
              inclusionTemplates.map((t) => renderTemplateItem(t, 'inclusion'))
            )}
          </div>
        </div>

        {/* Exclusions */}
        <div>
          <Label className="text-sm font-semibold text-red-700 mb-3 block flex items-center gap-2">
            <XSquare className="h-4 w-4" /> Exclusions
          </Label>
          <div className="space-y-1 max-h-72 overflow-y-auto pr-2 bg-red-50/50 p-3 rounded-xl border border-red-100">
            {exclusionTemplates.length === 0 ? (
              <p className="text-sm text-slate-500 italic py-4 text-center">No exclusions available. Add some below.</p>
            ) : (
              exclusionTemplates.map((t) => renderTemplateItem(t, 'exclusion'))
            )}
          </div>
        </div>
      </div>

      {/* Selected items — drag to reorder; this is what appears in the PDF */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <Label className="text-sm font-semibold text-emerald-700 mb-3 block">
            Selected Inclusions ({selectedInclusions.length}) — Drag to reorder
          </Label>
          <div className="bg-white p-3 rounded-xl border border-emerald-100" data-testid="selected-inclusions-list">
            <SortableSelectedList
              items={selectedInclusions}
              accent="inclusion"
              onChange={(next) => onReorderInclusions && onReorderInclusions(next)}
            />
          </div>
        </div>
        <div>
          <Label className="text-sm font-semibold text-rose-700 mb-3 block">
            Selected Exclusions ({selectedExclusions.length}) — Drag to reorder
          </Label>
          <div className="bg-white p-3 rounded-xl border border-rose-100" data-testid="selected-exclusions-list">
            <SortableSelectedList
              items={selectedExclusions}
              accent="exclusion"
              onChange={(next) => onReorderExclusions && onReorderExclusions(next)}
            />
          </div>
        </div>
      </div>

      {/* Add New Template */}
      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
        <Label className="text-sm font-semibold text-slate-700 mb-3 block">Add New Template Item</Label>
        <div className="flex gap-3">
          <Input 
            value={newText} 
            onChange={(e) => setNewText(e.target.value)} 
            placeholder="Enter new inclusion/exclusion..." 
            className="flex-1 h-10 rounded-lg bg-white" 
            onKeyPress={(e) => e.key === 'Enter' && handleAddNew()}
          />
          <select 
            value={newCategory} 
            onChange={(e) => setNewCategory(e.target.value)} 
            className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm font-medium"
          >
            <option value="inclusion">Inclusion</option>
            <option value="exclusion">Exclusion</option>
          </select>
          <Button 
            type="button" 
            onClick={handleAddNew} 
            className="bg-primary-700 hover:bg-primary-800 text-white rounded-lg px-4"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
      </div>
    </div>
  );
}
